import React, { useEffect, useRef, useState } from "react";

interface MapComponentProps {
  onLocationSelect?: (location: {
    lat: number;
    lng: number;
    address?: string;
    address1?: string;
    address2?: string;
    city?: string;
    state?: string;
    country?: string;
    pincode?: string;
  }) => void;
  addressData?: {
    address1?: string;
    address2?: string;
    city?: string;
    state?: string;
    country?: string;
    pincode?: string;
  };
  height?: string;
  triggerSearch?: boolean; // New prop to control when to search
}

const MapComponent: React.FC<MapComponentProps> = ({
  onLocationSelect,
  addressData,
  height = "250px",
  triggerSearch = false, // Default is false - only search when explicitly triggered
}) => {
  const mapRef = useRef<google.maps.Map | null>(null);
  const markerRef = useRef<google.maps.Marker | null>(null);
  const geocoderRef = useRef<google.maps.Geocoder | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const lastSearchRef = useRef<string>("");
  
  const [isLoading, setIsLoading] = useState(true);
  const [searchError, setSearchError] = useState("");
  const [selectedLocation, setSelectedLocation] = useState({
    lat: 13.1121,
    lng: 80.1771,
    address: "Madhuravoyal, Tamil Nadu 600095",
  });

  // Initialize the map, marker & geocoder
  useEffect(() => {
    const init = async () => {
      if (!window.google?.maps) {
        console.error("Google Maps API not loaded");
        return;
      }
      const { Map } = await google.maps.importLibrary("maps") as google.maps.MapsLibrary;
      const { Geocoder } = await google.maps.importLibrary("geocoding") as google.maps.GeocodingLibrary;
      const { PlacesService } = await google.maps.importLibrary("places") as google.maps.PlacesLibrary;

      mapRef.current = new Map(document.getElementById("map")!, {
        center: { lat: selectedLocation.lat, lng: selectedLocation.lng },
        zoom: 14,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
        zoomControl: true,
      });

      geocoderRef.current = new Geocoder();
      markerRef.current = new google.maps.Marker({
        position: { lat: selectedLocation.lat, lng: selectedLocation.lng },
        map: mapRef.current,
        draggable: true,
        animation: google.maps.Animation.DROP,
      });

      // On dragend, reverse-geocode at the final marker spot
      markerRef.current.addListener("dragend", () => {
        const pos = markerRef.current!.getPosition()!;
        doReverseGeocode(pos.lat(), pos.lng());
      });

      // On map click, move marker & reverse-geocode
      mapRef.current.addListener("click", (e: google.maps.MapMouseEvent) => {
        if (!e.latLng) return;
        const lat = e.latLng.lat(), lng = e.latLng.lng();
        markerRef.current!.setPosition({ lat, lng });
        doReverseGeocode(lat, lng);
      });

      // Initialize Places Autocomplete
      if (inputRef.current) {
        autocompleteRef.current = new google.maps.places.Autocomplete(inputRef.current, {
          fields: ["address_components", "geometry", "formatted_address"],
        });
        
        // Listen for place selection
        autocompleteRef.current.addListener("place_changed", () => {
          const place = autocompleteRef.current!.getPlace();
          
          if (!place.geometry || !place.geometry.location) {
            setSearchError("Invalid place selection");
            setTimeout(() => setSearchError(""), 3000);
            return;
          }
          
          const lat = place.geometry.location.lat();
          const lng = place.geometry.location.lng();
          
          doReverseGeocode(lat, lng);
        });
      }

      setIsLoading(false);
    };

    if (window.google && google.maps) init();
    else {
      window.addEventListener("load", init);
      return () => window.removeEventListener("load", init);
    }
  }, []);

  // Update input field when addressData changes
  useEffect(() => {
    if (!addressData || !inputRef.current) return;
    
    const addressStr = [
      addressData.address1,
      addressData.address2,
      addressData.city,
      addressData.state,
      addressData.country,
      addressData.pincode,
    ].filter(Boolean).join(", ");
    
    if (addressStr.length > 5) {
      inputRef.current.value = addressStr;
    }
  }, [addressData]);

  // Search only when triggerSearch prop changes to true
  useEffect(() => {
    if (!triggerSearch || !addressData || !geocoderRef.current || !inputRef.current) return;
    
    const addressStr = [
      addressData.address1,
      addressData.address2,
      addressData.city,
      addressData.state,
      addressData.country,
      addressData.pincode,
    ].filter(Boolean).join(", ");
    
    if (addressStr.length < 5 || addressStr === lastSearchRef.current) return;
    
    inputRef.current.value = addressStr;
    lastSearchRef.current = addressStr;
    
    searchAddress(addressStr);
  }, [triggerSearch, addressData]);

  // Search an address string and update the map
  const searchAddress = (addressStr: string) => {
    if (!geocoderRef.current) return;
    
    setSearchError("");
    
    geocoderRef.current.geocode({ address: addressStr }, (results, status) => {
      if (status === google.maps.GeocoderStatus.OK && results?.[0]) {
        const loc = results[0].geometry.location!;
        doReverseGeocode(loc.lat(), loc.lng());
      } else {
        setSearchError(`Search failed: ${status}`);
        setTimeout(() => setSearchError(""), 3000);
      }
    });
  };

  // Shared geocode → map + marker + address/components
  const doReverseGeocode = (lat: number, lng: number) => {
    if (!geocoderRef.current || !mapRef.current) return;
    setSearchError("");

    // Immediately recenter & move marker
    mapRef.current.setCenter({ lat, lng });
    markerRef.current!.setPosition({ lat, lng });

    geocoderRef.current.geocode({ location: { lat, lng } }, (results, status) => {
      if (status !== google.maps.GeocoderStatus.OK || !results?.[0]) {
        setSearchError(`Geocode failed: ${status}`);
        setTimeout(() => setSearchError(""), 3000);
        return;
      }

      // Pull out components
      const comp: Record<string, string> = {};
      results[0].address_components.forEach(c =>
        c.types.forEach(t => comp[t] = c.long_name)
      );

      // More robust address1 construction - prioritize more specific components
      let address1 = '';
      if (comp.street_number && comp.route) {
        address1 = `${comp.street_number} ${comp.route}`;
      } else if (comp.route) {
        address1 = comp.route;
      } else if (comp.premise) {
        address1 = comp.premise;
      } else if (comp.point_of_interest) {
        address1 = comp.point_of_interest;
      } else if (comp.establishment) {
        address1 = comp.establishment;
      }

      // Enhanced address2 determination with multiple fallbacks
      let address2 = '';
      
      // Try different levels of sublocalitiy first
      if (comp.sublocality_level_1) {
        address2 = comp.sublocality_level_1;
      } else if (comp.sublocality_level_2) {
        address2 = comp.sublocality_level_2;
      } else if (comp.sublocality) {
        address2 = comp.sublocality;
      } else if (comp.neighborhood) {
        address2 = comp.neighborhood;
      } else if (comp.administrative_area_level_3) {
        address2 = comp.administrative_area_level_3;
      }

      const loc = {
        lat, lng,
        address: results[0].formatted_address,
        address1,
        address2,
        city: comp.locality || comp.administrative_area_level_2 || "",
        state: comp.administrative_area_level_1 || "",
        country: comp.country || "",
        pincode: comp.postal_code || "",
      };

      setSelectedLocation(loc);
      if (inputRef.current) inputRef.current.value = loc.address;
      
      // Send the location data back to the parent component
      onLocationSelect?.(loc);
    });
  };

  // Search box geocode triggered by button or Enter key
  const handleSearch = () => {
    const q = inputRef.current!.value.trim();
    if (!q || !geocoderRef.current) return;
    
    lastSearchRef.current = q;
    searchAddress(q);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  return (
    <div className="w-full relative" style={{ height }}>
      {/* Search Box */}
      <div
        className="absolute top-4 left-1/2 transform -translate-x-1/2 z-10 w-[90%] sm:w-3/4 md:w-1/2 lg:w-1/3 xl:w-1/4"
        style={{ minWidth: 280, maxWidth: 600 }}
      >
        <div className="relative">
          <div className="flex bg-white shadow-md rounded-lg overflow-hidden border border-gray-300">
            <input
              ref={inputRef}
              type="text"
              placeholder="Search Location"
              className="p-3 w-full focus:outline-none"
              onKeyDown={handleKeyPress}
            />
            <button
              onClick={handleSearch}
              type="button"
              className="bg-blue-500 text-white px-4 hover:bg-blue-600 focus:outline-none"
            >
              Search
            </button>
          </div>
          {searchError && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-red-100 text-red-700 p-2 rounded text-sm">
              {searchError}
            </div>
          )}
        </div>
      </div>

      {/* Map Container */}
      <div
        id="map"
        className="w-full h-full rounded-lg"
        style={{
          filter: isLoading ? "blur(2px)" : undefined,
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
        }}
      />

      {/* Loading Overlay */}
      {isLoading && (
        <div className="absolute inset-0 bg-white bg-opacity-70 flex items-center justify-center rounded-lg">
          <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
        </div>
      )}
      
      {/* Selected Location Info */}
      {/* {selectedLocation && (
        <div className="absolute bottom-4 left-4 right-4 bg-white bg-opacity-90 p-3 rounded-lg shadow-md text-sm max-h-24 overflow-y-auto">
          <div className="text-gray-500 mt-1">
            Lat: {selectedLocation.lat.toFixed(6)}, Lng: {selectedLocation.lng.toFixed(6)}
          </div>
          <div className="font-medium">{selectedLocation.address}</div>
        </div>
      )} */}
    </div>
  );
};

export default MapComponent;